/** NimoOS's approved raster identity, shared by the desktop and application chrome. */
import type { CSSProperties } from 'react';
import './brand.css';

export const BRAND = {
  oatmeal: '#F6F0E7',
  lavender: '#C7B5DD',
  peach: '#EABBA5',
  plum: '#433849',
  // Legacy colour keys remain available to callers during the rebrand.
  cream: '#F6F0E7',
  orange: '#EABBA5',
  mint: '#C7B5DD',
  charcoal: '#433849',
};

type MarkProps = { size?: number; title?: string; className?: string; style?: CSSProperties };

export function Mark({ size = 64, title, className, style }: MarkProps) {
  return <img
    src="/brand/nimu-ghost.webp"
    width={size}
    height={size}
    className={['nimu-mark', className].filter(Boolean).join(' ')}
    style={style}
    alt={title ?? ''}
    aria-hidden={title ? undefined : true}
    draggable={false}
    decoding="async"
  />;
}

export function SimpleMark({ size = 24, title }: { size?: number; title?: string }) {
  return <Mark size={size} title={title} />;
}

export function Wordmark({ height = 32, title = 'NimoOS', className }: { height?: number; title?: string; className?: string }) {
  return <span
    className={['nimu-wordmark', className].filter(Boolean).join(' ')}
    style={{ height, fontSize: height * 0.94 }}
    role="img"
    aria-label={title}
  ><span aria-hidden="true">nimo<span className="nimu-wordmark__os">OS</span></span></span>;
}

export type IconName =
  | 'playground' | 'assistants' | 'prompts' | 'files' | 'notes'
  | 'miner' | 'train' | 'datasets' | 'models' | 'system' | 'tasks'
  | 'network' | 'ranks' | 'wallet' | 'readme' | 'settings' | 'trash'
  | 'token' | 'convert';

type AtlasIcon = 'chat' | 'assistants' | 'models' | 'prompts' | 'files' | 'notes'
  | 'miner' | 'wallet' | 'network' | 'leaderboard' | 'system' | 'settings';

/** The twelve atlas cells match the approved desktop, including optical centering. */
const ICON_ART: Record<IconName, AtlasIcon> = {
  playground: 'chat', assistants: 'assistants', models: 'models', prompts: 'prompts',
  files: 'files', notes: 'notes', miner: 'miner', wallet: 'wallet',
  network: 'network', ranks: 'leaderboard', system: 'system', settings: 'settings',
  train: 'assistants', datasets: 'files', tasks: 'system', readme: 'notes',
  trash: 'files', token: 'wallet', convert: 'wallet',
};

export function AppIcon({ name, size = 40, className }: { name: IconName; size?: number; className?: string }) {
  const art = ICON_ART[name];
  return <span
    className={['nimu-app-icon', className].filter(Boolean).join(' ')}
    style={{ width: size, height: size }}
    data-icon={name}
    aria-hidden="true"
  ><span className={'nimu-app-icon__sprite nimu-icon-' + art} /></span>;
}
